import { faSearch, faUndo } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { withTranslation } from "react-i18next";
import { getExchangeRateV4, getSpecialCustomerUpdatedByUsers } from "../../api/IrsClient";
import { COUNTRY_OPTION, EXCHANGE_RATE_TYPE, MAX_MOBILE_NUMBER_LENGHT, MAX_MONTH_RANGE } from "../../constant";
import { monthRangeIsMoreThanMaxMonthRange } from "../../utils/datetime";
import DateRangePicker from "../common/DateRangePicker";

const DATE_FORMAT = "yyyy-MM-dd HH:mm";
const START_OF_TODAY_DATE = moment().startOf("day").toDate();
const MAX_DATE = moment().endOf("day").toDate();

const defaultOption = {
  value: "",
  label: "All"
};

const defaultFormValue = {
  mobile_number: null,
  country: defaultOption.value,
  exchange_rate_label: defaultOption.value,
  updated_date: {
    from: START_OF_TODAY_DATE,
    to: MAX_DATE
  },
  updated_by: defaultOption.value
};

function SpecialCustomerSearchForm({
  isLoading,
  onClickSearch,
}) {
  const [countryOptions] = useState([defaultOption, ...COUNTRY_OPTION]);
  const [labelOptions, setLabelOptions] = useState([defaultOption]);
  const [updatedByOptions, setUpdatedByOptions] = useState([defaultOption]);
  const [specialExchangeRates, setSpecialExchangeRates] = useState([]);

  const {
    reset,
    watch,
    control,
    register,
    setValue,
    getValues,
    formState: { errors },
    handleSubmit
  } = useForm({
    defaultValues: defaultFormValue,
    mode: "onChange"
  });

  // to re-render value when select update date
  watch("updated_date");
  const watchedCountry = watch("country");

  // init
  useEffect(() => {
    callApiExchangeRates();
    callApiGetUpdatedBy();
  }, [])

  useEffect(() => {
    if (watchedCountry) {
      const matchedSpecialRate = specialExchangeRates.filter(rate => rate.country_code === watchedCountry);
      setLabelOptions([defaultOption, ...mapExchangeRateToOptions(matchedSpecialRate)]);
    } else {
      setLabelOptions([defaultOption]);
    }
  }, [watchedCountry, specialExchangeRates])

  async function callApiExchangeRates() {
    try {
      const request = {
        is_only_enable: true,
        type: "SPECIAL_CUSTOMER"
      };
      const { data: { status, data } } = await getExchangeRateV4(request);
      if (status.code === "0") {
        setSpecialExchangeRates(data.exchange_rates);
      }
    } catch (error) {

    }
  }

  async function callApiGetUpdatedBy() {
    const { data: { data } } = await getSpecialCustomerUpdatedByUsers();
    const userOptions = data.users.map(u => ({ label: u, value: u }));
    setUpdatedByOptions([defaultOption, ...userOptions]);
  }

  function onChangeMobileNumber(e) {
    const value = e.target.value.replace(/\D/g, "").slice(0, MAX_MOBILE_NUMBER_LENGHT);
    e.target.value = value;
  }

  function onChangeUpdateDate(from, to) {
    from = from || START_OF_TODAY_DATE.valueOf();
    to = to || MAX_DATE.valueOf();
    setValue("updated_date", { from: new Date(from), to: new Date(to) });
  }

  function onSubmitHandler(formValue) {
    onClickSearch(formValue);
  }

  function handleOnClear() {
    reset();
    setValue("updated_date", { from: null, to: null });
  }

  function mapExchangeRateToOptions(exchangeRates) {
    if (exchangeRates) {
      return exchangeRates.map(rate => ({ label: rate.label, value: rate.label }));
    } else {
      return []
    }
  }

  function mapOptions(options) {
    return options.map(o => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ));
  }

  function validateDateRange(from, to) {
    if (monthRangeIsMoreThanMaxMonthRange(from, to)) {
      return "Date range must not be more than " + MAX_MONTH_RANGE + " months";
    }
    return true;
  }

  return (
    <div className="card card-secondary">
      <div className="card-header">
        <h5>Remittance Configuration - Special Customer</h5>
      </div>

      <form
        onSubmit={handleSubmit(onSubmitHandler)}
        data-testid="special-customer-form"
        autoComplete="off"
      >
        <div className="p-4">
          <div className="row">
            <div className="col-sm-12">

              <div className="form-group row">
                <label className="col-form-label col-sm-2 text-lg-right">
                  Mobile no. (start with)
                </label>
                <div className={cn("col-sm-4 p-0", {
                  "form-error": errors.mobile_number
                })}>
                  <input
                    className="form-control"
                    data-testid="mobile-number-input"
                    type="text"
                    {...register("mobile_number", {
                      minLength: {
                        value: 3,
                        message: "Must be at least 3 characters long"
                      },
                      onChange: onChangeMobileNumber
                    })}
                  />
                  {errors.mobile_number && (
                    <span className="input-error-message">
                      {errors.mobile_number.message}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-group row mb-4">
                <label className="col-form-label col-sm-2 text-lg-right">
                  Country
                </label>
                <div className="col-sm-4 p-0">
                  <select
                    data-testid="country-select"
                    className="form-control"
                    {...register("country")}
                  >
                    {mapOptions(countryOptions)}
                  </select>
                </div>
                <label className="col-form-label col-sm-2 text-lg-right">
                  Exchange label
                </label>
                <div className="col-sm-4 p-0">
                  <select
                    data-testid="exchange-label-select"
                    className="form-control"
                    {...register("exchange_rate_label")}
                    disabled={!Boolean(watchedCountry)}
                  >
                    {mapOptions(labelOptions)}
                  </select>
                </div>
              </div>

              <div className="form-group row mb-4">
                <label className="col-form-label col-sm-2 text-lg-right">
                  Update Date
                </label>
                <div className="col-sm-4 p-0">
                  <DateRangePicker
                    control={control}
                    showTime
                    dateFormat={DATE_FORMAT}
                    maxDate={MAX_DATE}
                    prefixFieldName={"updated_date"}
                    startDate={getValues("updated_date.from")}
                    endDate={getValues("updated_date.to")}
                    onChange={onChangeUpdateDate}
                    errors={errors}
                    rules={{
                      from: {
                        validate: {
                          range: (v) => validateDateRange(v, getValues("updated_date.to")),
                        },
                      }
                    }}
                  />
                </div>
              </div>

              <div className="form-group row mb-4">
                <label className="col-form-label col-sm-2 text-lg-right">
                  Last update by
                </label>
                <div className="col-sm-4 p-0">
                  <select
                    data-testid="updated-by-select"
                    className="form-control"
                    {...register("updated_by")}
                  >
                    {mapOptions(updatedByOptions)}
                  </select>
                </div>
              </div>

              <div className="row justify-content-center">
                <button
                  data-testid="special-customer-search-button"
                  className="btn btn-warning"
                  disabled={isLoading}
                  type="submit"
                >
                  <FontAwesomeIcon icon={faSearch} /> Search
                </button>
                <button
                  data-testid="special-customer-clear-button"
                  type="button"
                  className="btn btn-secondary ml-1"
                  onClick={handleOnClear}
                >
                  <FontAwesomeIcon icon={faUndo} /> Clear
                </button>
              </div>

            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default withTranslation()(SpecialCustomerSearchForm);
